"use client"

import type { AttackType, DefenseType } from "@/types/hero.types"
import { getDamageMultiplier, getArmorReduction } from "@/systems/damage-system"

interface ArmorTypeTooltipProps {
  armorType: DefenseType
  armorValue?: number
  fullWidth?: boolean
}

// All attack types for the table
const attackTypes: AttackType[] = [
  "normal",
  "pierce",
  "siege",
  "hero",
  "magic",
  "spells",
  "chaos",
]

// Descriptions for each armor type
const armorTypeDescriptions: Record<DefenseType, string> = {
  unarmored: "No armor protection. Vulnerable to pierce attacks.",
  light: "Light armor. Very vulnerable to pierce, resistant to siege.",
  medium: "Balanced protection. Weak to normal, strong vs pierce.",
  heavy: "Heavy armor. Strong vs pierce, very weak to magic.",
  fortified: "Fortified structures. Only siege deals bonus damage.",
  hero: "Hero armor. Balanced resistances, weak to magic.",
  divine: "Divine protection. Nearly immune to all except chaos.",
}

// Colors for armor type names
const defenseTypeColors: Record<DefenseType, string> = {
  unarmored: "#AAAAAA",
  light: "#90EE90",
  medium: "#87CEEB",
  heavy: "#CD853F",
  fortified: "#708090",
  hero: "#FF6B6B",
  divine: "#FFD700",
}

const formatTypeName = (type: string): string => {
  return type.charAt(0).toUpperCase() + type.slice(1)
}

// Invert effectiveness color for armor (high damage = bad for armor)
const getArmorEffectivenessColor = (multiplier: number): string => {
  if (multiplier >= 2.0) return "#ff0000" // Very vulnerable - red
  if (multiplier >= 1.5) return "#ff6666" // Vulnerable - light red
  if (multiplier >= 1.0) return "#ffffff" // Normal - white
  if (multiplier >= 0.5) return "#88ff88" // Resistant - light green
  if (multiplier >= 0.35) return "#00ff00" // Very resistant - green
  return "#00ffff" // Immune - cyan
}

export const ArmorTypeTooltip = ({ armorType, armorValue, fullWidth = false }: ArmorTypeTooltipProps) => {
  const armorReduction = armorValue !== undefined ? getArmorReduction(armorValue) : null
  const armorPercent = armorReduction !== null ? Math.round(armorReduction * 100) : null

  return (
    <div className={fullWidth ? "w-full p-3" : "w-40 p-2"}>
      {/* Header */}
      <div className="flex items-center gap-1.5 mb-2 pb-1.5 border-b border-white/20">
        <span
          className={fullWidth ? "font-bold text-sm" : "font-bold text-xs"}
          style={{ color: defenseTypeColors[armorType] }}
        >
          {formatTypeName(armorType)}
        </span>
        <span className={fullWidth ? "text-white/70 text-sm" : "text-white/70 text-xs"}>Armor</span>
      </div>

      {/* Damage received table */}
      <div className={fullWidth ? "space-y-1 text-sm" : "space-y-0.5 text-[10px]"}>
        <div className={fullWidth ? "text-white/40 text-xs mb-1" : "text-white/40 text-[9px] mb-1"}>Damage received from:</div>
        {attackTypes.map((atkType) => {
          const multiplier = getDamageMultiplier(atkType, armorType)
          const percent = Math.round(multiplier * 100)
          const color = getArmorEffectivenessColor(multiplier)

          return (
            <div key={atkType} className="flex justify-between items-center">
              <span className="text-white/60">{formatTypeName(atkType)}</span>
              <span className="font-semibold" style={{ color }}>
                {percent}%
              </span>
            </div>
          )
        })}
      </div>

      {/* Armor value and reduction */}
      {armorValue !== undefined && armorPercent !== null && (
        <div className="mt-2 pt-1.5 border-t border-white/20">
          <div className={fullWidth ? "flex justify-between items-center text-sm" : "flex justify-between items-center text-[10px]"}>
            <span className="text-white/60">Armor value</span>
            <span className="font-semibold text-amber-400">
              {armorValue} ({armorPercent}% reduction)
            </span>
          </div>
        </div>
      )}

      {/* Description */}
      <div className="mt-2 pt-1.5 border-t border-white/20">
        <p className={fullWidth ? "text-xs text-white/50 leading-tight" : "text-[9px] text-white/50 leading-tight"}>
          {armorTypeDescriptions[armorType]}
        </p>
      </div>
    </div>
  )
}
